import React, { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Select from "react-select";
import {
  Card,
  CardBody,
  Col,
  Container,
  Form,
  Input,
  Label,
  Row,
  Button,
  Table,
} from "reactstrap";
import { savePurchaseRequest, getNextOrderNo } from "../../components/Api/GmsPurchaseRequest";
import { getBusinesses } from "../../components/Api/GmsBusiness";
import { getBranches } from "../../components/Api/GmsBranch";
import { searchSuppliers } from "../../components/Api/GmsSupplier";
import { getProducts } from "../../components/Api/GmsProduct";
import { getActiveUnits } from "../../components/Api/GmsUnits";
import { getTaxes } from "../../components/Api/GmsTax";
import { Plus, Minus } from "lucide-react";
import { ClipLoader } from "react-spinners";
import Swal from "sweetalert2";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import {
  hasPagePermissionWithAdmin,
  hasEditOrHigherPermission,
  isUserAdmin
} from "../../components/Permission/PermissionService";
import Breadcrumb from "../../components/Common/Breadcrumb";

// Custom styles for Select components
const selectStyles = {
  control: (provided, state) => ({
    ...provided,
    minHeight: '38px',
    borderColor: state.isFocused ? '#556ee6' : '#ced4da',
    boxShadow: state.isFocused ? '0 0 0 0.2rem rgba(85, 110, 230, 0.25)' : 'none',
  }),
  menu: (provided) => ({
    ...provided,
    zIndex: 9999,
    maxHeight: 200,
  }),
  menuList: (provided) => ({
    ...provided,
    maxHeight: 180,
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected ? '#556ee6' : state.isFocused ? '#f8f9fa' : 'white',
    color: state.isSelected ? 'white' : '#495057',
    '&:hover': {
      backgroundColor: state.isSelected ? '#556ee6' : '#e9ecef',
    },
  }),
};

const defaultForm = {
  PurchaseRequest_Id: null,
  PurchaseRequest_OrderNo: "",
  PurchaseRequest_RefNo: "",
  PurchaseRequest_Supplier_Id: null,
  PurchaseRequest_InvoiceType_Id: 1, // 1 = Cash, 2 = Credit
  PurchaseRequest_Date: new Date(),
  PurchaseRequest_DueDate: new Date(),
  PurchaseRequest_ArrivalDate: null,
  PurchaseRequest_Amount: 0,
  PurchaseRequest_DiscountPercent: 0,
  PurchaseRequest_DiscountAmount: 0,
  PurchaseRequest_OtherAmount: 0,
  PurchaseRequest_TaxAmount: 0,
  PurchaseRequest_NetAmount: 0,
  PurchaseRequest_Notes: "",
  PurchaseRequest_TotalQty: 0,
  PurchaseRequest_Branch_Id: null,
  PurchaseRequest_TotalAmount: 0,
  PurchaseRequest_CreatedDate: new Date().toISOString(),
  PurchaseRequest_CreatedBy: 1,
  PurchaseRequest_Business_Id: null,
  PurchaseRequest_Is_Received: false,
  Details: [],
};

const defaultDetail = {
  PurchaseRequestDet_Id: null,
  PurchaseRequestDet_Product_Id: null,
  PurchaseRequestDet_Unit_Id: 1,
  PurchaseRequestDet_Quantity: 1,
  PurchaseRequestDet_BonusQuantity: 0,
  PurchaseRequestDet_UnitPrice: 0,
  PurchaseRequestDet_DiscountPercent: 0,
  PurchaseRequestDet_TotalAmount: 0,
  PurchaseRequestDet_NetAmount: 0,
  PurchaseRequestDet_TaxPercent: 0,
  Product_NameEn: "",
  Unit_NameEn: "",
  TaxDetails: [],
};

// Rounding helper: standard half-up to 2 decimals
const roundHalfUp = (value, decimals = 2) => {
  const factor = Math.pow(10, decimals);
  return Math.round((Number(value) + Number.EPSILON) * factor) / factor;
};

// Session management
const getUserSession = () => {
  const authUser = JSON.parse(localStorage.getItem("authUser") || "{}");
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  return {
    userId: authUser.userId || 1,
    userName: authUser.userName || "System",
    branchId: authUser.branchId || user.user_Branch_Id || localStorage.getItem("userBranchId") || null,
    companyId: authUser.companyId || user.user_Company_Id || localStorage.getItem("userCompanyId") || null,
  };
};

export default function AddPurchaseOrderPage() {
  const navigate = useNavigate();

  // Check permissions first
  const permissions = JSON.parse(localStorage.getItem('permissions') || '[]');
  const hasPermission = hasPagePermissionWithAdmin(permissions, '/GmsAddPurchaseOrder');
  const hasEditOrHigher = hasEditOrHigherPermission(permissions, '/GmsAddPurchaseOrder');
  const isAdmin = isUserAdmin();

  const [businesses, setBusinesses] = useState([]);
  const [branches, setBranches] = useState([]);
  const [products, setProducts] = useState([]);
  const [units, setUnits] = useState([]);
  const [taxes, setTaxes] = useState([]);
  const [form, setForm] = useState(defaultForm);
  const [details, setDetails] = useState([{ ...defaultDetail }]);

  const selectedBranchObj = branches.find(b => b.branch_Id === form.PurchaseRequest_Branch_Id);
  const isPurchaseAllowed = selectedBranchObj ? selectedBranchObj.branch_AllowPurchase === true : false;

  const [saveLoading, setSaveLoading] = useState(false);
  const [formKey, setFormKey] = useState(0);

  // Ref to track the last business ID we generated order number for (to prevent duplicate calls)
  const lastGeneratedBusinessIdRef = useRef(null);

  // Supplier search states
  const [supplierSearchOptions, setSupplierSearchOptions] = useState([]);
  const [supplierSearchLoading, setSupplierSearchLoading] = useState(false);
  const [supplierSearchTimeout, setSupplierSearchTimeout] = useState(null);
  const [selectedSupplierOption, setSelectedSupplierOption] = useState(null);

  useEffect(() => {
    if (hasPermission && hasEditOrHigher) {
      loadBusinesses();
      loadBranches();
      loadProducts();
      loadUnits();
      loadTaxes();
    } else if (!hasPermission) {
      window.location.href = '/Dashboard';
    }
  }, [hasPermission, hasEditOrHigher]);

  // Auto-populate company if user has companyId
  useEffect(() => {
    const userCompanyId = localStorage.getItem("userCompanyId");
    if (userCompanyId && businesses.length > 0) {
      const companyExists = businesses.find(b => b.business_Id === parseInt(userCompanyId));
      if (companyExists && !form.PurchaseRequest_Business_Id) {
        setForm(prev => ({
          ...prev,
          PurchaseRequest_Business_Id: parseInt(userCompanyId)
        }));
      }
    }
  }, [businesses]);

  // Auto-populate branch if user has branchId
  useEffect(() => {
    const userBranchId = localStorage.getItem("userBranchId");
    if (userBranchId && branches.length > 0 && form.PurchaseRequest_Business_Id) {
      const branchExists = branches.find(b => b.branch_Id === parseInt(userBranchId) && b.branch_Buisness_Id === form.PurchaseRequest_Business_Id);
      if (branchExists && !form.PurchaseRequest_Branch_Id) {
        setForm(prev => ({
          ...prev,
          PurchaseRequest_Branch_Id: parseInt(userBranchId)
        }));
      }
    }
  }, [branches, form.PurchaseRequest_Business_Id]);

  // Auto-generate order number when business is selected
  useEffect(() => {
    if (
      form.PurchaseRequest_Business_Id &&
      !form.PurchaseRequest_OrderNo &&
      lastGeneratedBusinessIdRef.current !== form.PurchaseRequest_Business_Id
    ) {
      const generateOrderNo = async () => {
        try {
          lastGeneratedBusinessIdRef.current = form.PurchaseRequest_Business_Id;
          const result = await getNextOrderNo(form.PurchaseRequest_Business_Id);
          if (result.nextOrderNo) {
            setForm(prev => ({
              ...prev,
              PurchaseRequest_OrderNo: result.nextOrderNo
            }));
          }
        } catch (error) {
          console.error("Error getting next order number:", error);
          lastGeneratedBusinessIdRef.current = null;
        }
      };
      generateOrderNo();
    }
  }, [form.PurchaseRequest_Business_Id, form.PurchaseRequest_OrderNo]);

  const loadBusinesses = async () => {
    try {
      const data = await getBusinesses({ business_Active: true });
      const businessData = Array.isArray(data) ? data : (data.data || []);
      setBusinesses(businessData);
    } catch (error) {
      console.error("Error loading businesses:", error);
      Swal.fire("Error", "Failed to load companies", "error");
    }
  };

  const loadBranches = async () => {
    try {
      const data = await getBranches({ branch_Active: true });
      setBranches(data || []);
    } catch (error) {
      console.error("Error loading branches:", error);
      Swal.fire("Error", "Failed to load branches", "error");
    }
  };

  const loadProducts = async () => {
    try {
      const data = await getProducts({});
      setProducts(data || []);
    } catch (error) {
      console.error("Error loading products:", error);
      Swal.fire("Error", "Failed to load products", "error");
    }
  };

  const loadUnits = async () => {
    try {
      const data = await getActiveUnits();
      setUnits(data || []);
    } catch (error) {
      console.error("Error loading units:", error);
      Swal.fire("Error", "Failed to load units", "error");
    }
  };

  const loadTaxes = async () => {
    try {
      const data = await getTaxes({ tax_Active: true });
      setTaxes(data || []);
    } catch (error) {
      console.error("Error loading taxes:", error);
      Swal.fire("Error", "Failed to load taxes", "error");
    }
  };

  // Manual search function for supplier search
  const searchSuppliersManually = async (inputValue) => {
    const trimmedInput = inputValue ? inputValue.trim() : '';

    if (!trimmedInput || trimmedInput.length < 3) {
      setSupplierSearchOptions([]);
      return;
    }

    try {
      setSupplierSearchLoading(true);
      const response = await searchSuppliers(trimmedInput, 1, 20);

      if (!response || !response.items || !Array.isArray(response.items)) {
        setSupplierSearchOptions([]);
        return;
      }

      const formattedOptions = response.items.map(supplier => {
        const hasArabicName = supplier.supplier_NameAr && supplier.supplier_NameAr.trim() !== '';
        const hasEnglishName = supplier.supplier_NameEn && supplier.supplier_NameEn.trim() !== '';
        const containsArabicChars = /[\u0600-\u06FF]/.test(trimmedInput);
        const searchInArabic = hasArabicName && (
          supplier.supplier_NameAr.includes(trimmedInput) ||
          (containsArabicChars && supplier.supplier_NameAr.includes(trimmedInput))
        );

        let primaryName, secondaryName;
        if (searchInArabic && hasArabicName) {
          primaryName = supplier.supplier_NameAr;
          secondaryName = hasEnglishName ? supplier.supplier_NameEn : null;
        } else if (hasEnglishName) {
          primaryName = supplier.supplier_NameEn;
          secondaryName = hasArabicName ? supplier.supplier_NameAr : null;
        } else if (hasArabicName) {
          primaryName = supplier.supplier_NameAr;
          secondaryName = null;
        } else {
          primaryName = 'N/A';
          secondaryName = null;
        }

        let label = primaryName;
        if (secondaryName && secondaryName.trim() !== '') {
          label = `${primaryName} — ${secondaryName}`;
        }
        label += ` — ${supplier.supplier_Code || supplier.supplier_ContactNo || 'N/A'}`;

        return {
          value: supplier.supplier_Id,
          label: label,
          supplier: supplier
        };
      });

      setSupplierSearchOptions(formattedOptions);
    } catch (error) {
      console.error('Error searching suppliers:', error);
      setSupplierSearchOptions([]);
    } finally {
      setSupplierSearchLoading(false);
    }
  };

  const handleBusinessChange = async (businessId) => {
    setForm(prev => ({
      ...prev,
      PurchaseRequest_Business_Id: businessId,
      PurchaseRequest_Branch_Id: null
    }));

    if (businessId) {
      try {
        const result = await getNextOrderNo(businessId);
        if (result.nextOrderNo) {
          setForm(prev => ({
            ...prev,
            PurchaseRequest_OrderNo: result.nextOrderNo
          }));
        }
      } catch (error) {
        console.error("Error getting next order number:", error);
      }
    }
  };

  const handleFormChange = (field, value) => {
    setForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleDetailChange = async (index, field, value) => {
    // Validate quantity limits
    if (field === 'PurchaseRequestDet_Quantity') {
      if (value > 5000) {
        Swal.fire({
          icon: 'warning',
          title: 'Quantity Limit Exceeded',
          text: 'Maximum quantity allowed is 5000. Please enter a value between 0 and 5000.',
          confirmButtonText: 'OK'
        });
        value = 5000;
      }
    } else if (field === 'PurchaseRequestDet_BonusQuantity') {
      if (value > 1000) {
        Swal.fire({
          icon: 'warning',
          title: 'Bonus Quantity Limit Exceeded',
          text: 'Maximum bonus quantity allowed is 1000. Please enter a value between 0 and 1000.',
          confirmButtonText: 'OK'
        });
        value = 1000;
      }
    }

    const newDetails = [...details];
    newDetails[index] = { ...newDetails[index], [field]: value };

    // Auto-fetch product details
    if (field === 'PurchaseRequestDet_Product_Id' && value) {
      const product = products.find(p => p.product_Id === value);
      if (product) {
        const unit = units.find(u => u.value == product.unit_Id || u.unit_Id == product.unit_Id);
        const unitName = unit ? unit.label.split(' - ')[1] || unit.label : "Unit";

        let taxPercent = 0;
        let taxDetails = [];

        const isFractionalUnit = !!(unit?.allowFractional);

        newDetails[index] = {
          ...newDetails[index],
          PurchaseRequestDet_UnitPrice: product.product_CostPrice || 0,
          PurchaseRequestDet_TaxPercent: taxPercent,
          PurchaseRequestDet_Unit_Id: product.unit_Id || 1,
          Product_NameEn: product.product_NameEn,
          Unit_NameEn: unitName,
          TaxDetails: taxDetails,
          // Track whether this unit allows fractional quantities
          allowFractional: isFractionalUnit
        };
      }
    }

    if (field === 'PurchaseRequestDet_TaxPercent') {
      newDetails[index] = {
        ...newDetails[index],
        TaxDetails: []
      };
    }

    // Calculate amounts
    if (field === 'PurchaseRequestDet_Quantity' || field === 'PurchaseRequestDet_BonusQuantity' ||
      field === 'PurchaseRequestDet_UnitPrice' || field === 'PurchaseRequestDet_DiscountPercent' ||
      field === 'PurchaseRequestDet_TaxPercent' || field === 'PurchaseRequestDet_Product_Id') {
      calculateDetailAmounts(newDetails, index);
      recalculateTotalsFromDetails(newDetails);
    }

    setDetails(newDetails);
  };

  const recalculateTotalsFromDetails = (detailsArray) => {
    let totalAmount = 0;
    let totalDiscount = 0;
    let totalTax = 0;
    let totalQty = 0;

    detailsArray.forEach((detail) => {
      if (detail && detail.PurchaseRequestDet_Product_Id) {
        const totalAmountValue = parseFloat(detail.PurchaseRequestDet_TotalAmount) || 0;
        const discountAmount = parseFloat(detail.PurchaseRequestDet_DiscountAmount) || 0;
        const taxAmount = parseFloat(detail.PurchaseRequestDet_TaxAmount) || 0;
        const quantity = parseFloat(detail.PurchaseRequestDet_Quantity) || 0;

        totalAmount += totalAmountValue;
        totalDiscount += discountAmount;
        totalTax += taxAmount;
        totalQty += quantity;
      }
    });

    totalAmount = roundHalfUp(totalAmount, 2);
    totalDiscount = roundHalfUp(totalDiscount, 2);
    totalTax = roundHalfUp(totalTax, 2);
    totalQty = roundHalfUp(totalQty, 2);

    const netAmount = roundHalfUp(totalAmount + totalTax, 2);

    setForm(prev => ({
      ...prev,
      PurchaseRequest_TotalAmount: totalAmount,
      PurchaseRequest_Amount: totalAmount,
      PurchaseRequest_DiscountAmount: totalDiscount,
      PurchaseRequest_TaxAmount: totalTax,
      PurchaseRequest_NetAmount: netAmount,
      PurchaseRequest_TotalQty: totalQty
    }));
  };

  const calculateDetailAmounts = (detailsArray, index) => {
    const detail = detailsArray[index];
    const quantity = parseFloat(detail.PurchaseRequestDet_Quantity) || 0;
    const bonusQuantity = parseFloat(detail.PurchaseRequestDet_BonusQuantity) || 0;
    const unitPrice = parseFloat(detail.PurchaseRequestDet_UnitPrice) || 0;
    const discountPercent = parseFloat(detail.PurchaseRequestDet_DiscountPercent) || 0;
    const taxPercent = parseFloat(detail.PurchaseRequestDet_TaxPercent) || 0;

    if (quantity <= 0) {
      detailsArray[index] = {
        ...detail,
        PurchaseRequestDet_DiscountAmount: 0,
        PurchaseRequestDet_TotalAmount: 0,
        PurchaseRequestDet_TaxAmount: 0,
        PurchaseRequestDet_NetAmount: 0
      };
      return;
    }

    const discountedUnitPrice = roundHalfUp(unitPrice * (1 - (discountPercent / 100)), 2);
    const discountAmount = roundHalfUp(quantity * unitPrice * (discountPercent / 100), 2);
    const lineTotalAmount = roundHalfUp(quantity * discountedUnitPrice, 2);
    const totalQuantity = quantity + bonusQuantity;
    const taxBase = totalQuantity * discountedUnitPrice;

    let taxComponentsSum = 0;
    if (Array.isArray(detail.TaxDetails) && detail.TaxDetails.length > 0) {
      taxComponentsSum = detail.TaxDetails.reduce((sum, t) => {
        const perc = parseFloat(t.Tax_Perc || t.tax_Perc || 0) || 0;
        const comp = roundHalfUp(taxBase * (perc / 100), 2);
        return sum + comp;
      }, 0);
    } else {
      taxComponentsSum = roundHalfUp(taxBase * (taxPercent / 100), 2);
    }
    const taxAmount = roundHalfUp(taxComponentsSum, 2);
    const netAmount = roundHalfUp(lineTotalAmount + taxAmount, 2);

    detailsArray[index] = {
      ...detail,
      PurchaseRequestDet_DiscountAmount: discountAmount,
      PurchaseRequestDet_TotalAmount: lineTotalAmount,
      PurchaseRequestDet_TaxAmount: taxAmount,
      PurchaseRequestDet_NetAmount: netAmount
    };
  };

  const addDetail = () => {
    setDetails([...details, { ...defaultDetail }]);
  };

  const removeDetail = (index) => {
    if (details.length > 1) {
      const newDetails = details.filter((_, i) => i !== index);
      setDetails(newDetails);
      recalculateTotalsFromDetails(newDetails);
    }
  };

  useEffect(() => {
    recalculateTotalsFromDetails(details);
  }, [details]);

  const handleSave = async () => {
    try {
      if (!isPurchaseAllowed) {
        Swal.fire("Error", "Purchases are not allowed for this branch", "error");
        return;
      }
      if (!form.PurchaseRequest_Business_Id) {
        Swal.fire("Error", "Please select a company", "error");
        return;
      }

      if (!form.PurchaseRequest_Supplier_Id) {
        Swal.fire("Error", "Please select a supplier", "error");
        return;
      }

      if (details.length === 0 || !details.some(detail => detail.PurchaseRequestDet_Product_Id)) {
        Swal.fire("Error", "Please add at least one product", "error");
        return;
      }

      const filteredDetails = details.filter(detail => detail.PurchaseRequestDet_Product_Id);
      if (filteredDetails.length === 0) {
        Swal.fire("Error", "Please add at least one product", "error");
        return;
      }

      setSaveLoading(true);
      const session = getUserSession();

      const saveData = {
        ...form,
        PurchaseRequest_OrderNo: form.PurchaseRequest_OrderNo,
        PurchaseRequest_CreatedBy: session.userId,
        PurchaseRequest_Is_Received: false,
        Details: filteredDetails.map(detail => {
          const detailObj = { ...detail };
          if (!detailObj.PurchaseRequestDet_Id || detailObj.PurchaseRequestDet_Id === 0) {
            delete detailObj.PurchaseRequestDet_Id;
          }
          return detailObj;
        }),
        TaxDetails: filteredDetails
          .filter(detail => detail.TaxDetails)
          .flatMap((detail, index) => {
            return detail.TaxDetails.map(tax => ({
              TempDetailId: index + 1,
              PurchaseRequestTaxDet_Tax_Id: tax.PurchaseRequestTaxDet_Tax_Id || tax.purchaseRequestTaxDet_Tax_Id || tax.tax_Id || tax.Tax_Id,
              Tax_NameEn: tax.Tax_NameEn || tax.tax_NameEn,
              Tax_Perc: tax.Tax_Perc || tax.tax_Perc
            }));
          })
      };

      if (!saveData.PurchaseRequest_Id || saveData.PurchaseRequest_Id === 0) {
        delete saveData.PurchaseRequest_Id;
      }

      const result = await savePurchaseRequest(saveData);

      const isSuccess = result && (
        (result.id > 0) ||
        (result.message && result.message.toLowerCase().includes("successfully"))
      );

      if (isSuccess) {
        Swal.fire({
          icon: "success",
          title: "Success",
          text: result.message || "Purchase order saved successfully!",
          confirmButtonText: "OK"
        }).then(() => {
          handleClear();
        });
      } else {
        Swal.fire("Error", result?.message || "Failed to save purchase order", "error");
      }
    } catch (error) {
      console.error("Error saving purchase order:", error);
      Swal.fire("Error", "Failed to save purchase order", "error");
    } finally {
      setSaveLoading(false);
    }
  };

  const handleClear = () => {
    const userCompanyId = localStorage.getItem("userCompanyId");
    const userBranchId = localStorage.getItem("userBranchId");

    const resetFormData = {
      ...defaultForm,
      PurchaseRequest_Date: new Date(),
      PurchaseRequest_DueDate: new Date()
    };

    if (userCompanyId && businesses.length > 0) {
      const companyExists = businesses.find(b => b.business_Id === parseInt(userCompanyId));
      if (companyExists) {
        resetFormData.PurchaseRequest_Business_Id = parseInt(userCompanyId);

        if (userBranchId && branches.length > 0) {
          const branchExists = branches.find(b => b.branch_Id === parseInt(userBranchId) && b.branch_Buisness_Id === parseInt(userCompanyId));
          if (branchExists) {
            resetFormData.PurchaseRequest_Branch_Id = parseInt(userBranchId);
          }
        }
      }
    }

    setForm(resetFormData);
    setDetails([{ ...defaultDetail }]);
    setSelectedSupplierOption(null);
    setSupplierSearchOptions([]);
    lastGeneratedBusinessIdRef.current = null;
    setFormKey(prev => prev + 1);
  };

  // Options for dropdowns
  const businessOptions = useMemo(() => {
    return businesses.map(b => ({
      value: b.business_Id,
      label: `${b.business_Code || ''} - ${b.business_NameEn || ''}`.trim()
    }));
  }, [businesses]);

  const branchOptions = useMemo(() => {
    if (!form.PurchaseRequest_Business_Id) return [];
    return branches
      .filter(b => b.branch_Buisness_Id === form.PurchaseRequest_Business_Id)
      .map(b => ({
        value: b.branch_Id,
        label: `${b.branch_Code || ''} - ${b.branch_NameEn || ''}`.trim()
      }));
  }, [branches, form.PurchaseRequest_Business_Id]);

  const productOptions = useMemo(() => {
    return products.map(p => ({
      value: p.product_Id,
      label: `${p.product_Code || ''} - ${p.product_NameEn || ''}`.trim()
    }));
  }, [products]);

  const invoiceTypeOptions = [
    { value: 1, label: 'Cash' },
    { value: 2, label: 'Credit' }
  ];

  if (!hasPermission) {
    return null;
  }

  if (!hasEditOrHigher) {
    return (
      <div className="page-content">
        <Container fluid>
          <Breadcrumb title="Add Purchase Order" breadcrumbItem="Add Purchase Order" />
          <Card>
            <CardBody>
              <div className="text-center p-4">
                <p className="text-muted">You don't have permission to add purchase orders.</p>
              </div>
            </CardBody>
          </Card>
        </Container>
      </div>
    );
  }

  return (
    <div className="page-content">
      <style>{`
        input.no-spinner::-webkit-outer-spin-button,
        input.no-spinner::-webkit-inner-spin-button {
          -webkit-appearance: none;
          margin: 0;
        }
        input.no-spinner[type=number] {
          -moz-appearance: textfield;
        }
      `}</style>
      <Container fluid>
        <Breadcrumb title="Purchase Order Management" breadcrumbItem="Add Purchase Order" />

        <Row>
          <Col xs={12}>
            <Card>
              <CardBody>
                <Form onSubmit={(e) => { e.preventDefault(); handleSave(); }}>
                  {/* Basic Information */}
                  <Row>
                    <Col md={3}>
                      <div className="mb-3">
                        <Label>Company <span className="text-danger">*</span></Label>
                        <Select
                          key={`business-${formKey}`}
                          value={businessOptions.find(option => option.value === form.PurchaseRequest_Business_Id) || null}
                          onChange={(option) => handleBusinessChange(option?.value || null)}
                          options={businessOptions}
                          placeholder="Select Company"
                          styles={selectStyles}
                          isClearable
                          isSearchable
                          maxMenuHeight={200}
                          isDisabled={!!localStorage.getItem("userCompanyId")}
                        />
                      </div>
                    </Col>
                    <Col md={3}>
                      <div className="mb-3">
                        <Label>Branch</Label>
                        <Select
                          key={`branch-${formKey}`}
                          value={branchOptions.find(option => option.value === form.PurchaseRequest_Branch_Id) || null}
                          onChange={(option) => handleFormChange("PurchaseRequest_Branch_Id", option?.value || null)}
                          options={branchOptions}
                          placeholder="Select Branch"
                          styles={selectStyles}
                          isClearable
                          isSearchable
                          maxMenuHeight={200}
                          isDisabled={!form.PurchaseRequest_Business_Id || !!localStorage.getItem("userBranchId")}
                        />
                      </div>
                    </Col>
                    <Col md={3}>
                      <div className="mb-3">
                        <Label>Order Number</Label>
                        <Input
                          type="text"
                          value={form.PurchaseRequest_OrderNo}
                          readOnly
                          style={{ backgroundColor: '#f8f9fa' }}
                        />
                      </div>
                    </Col>
                    <Col md={3}>
                      <div className="mb-3">
                        <Label>Date <span className="text-danger">*</span></Label>
                        <DatePicker
                          selected={form.PurchaseRequest_Date}
                          onChange={(date) => handleFormChange("PurchaseRequest_Date", date)}
                          dateFormat="dd/MM/yyyy"
                          className="form-control"
                        />
                      </div>
                    </Col>
                  </Row>

                  <Row>
                    <Col md={3}>
                      <div className="mb-3">
                        <Label>Due Date</Label>
                        <DatePicker
                          selected={form.PurchaseRequest_DueDate}
                          onChange={(date) => handleFormChange("PurchaseRequest_DueDate", date)}
                          dateFormat="dd/MM/yyyy"
                          className="form-control"
                        />
                      </div>
                    </Col>
                    <Col md={3}>
                      <div className="mb-3">
                        <Label>Arrival Date</Label>
                        <DatePicker
                          selected={form.PurchaseRequest_ArrivalDate}
                          onChange={(date) => handleFormChange("PurchaseRequest_ArrivalDate", date)}
                          dateFormat="dd/MM/yyyy"
                          className="form-control"
                          isClearable
                        />
                      </div>
                    </Col>
                    <Col md={3}>
                      <div className="mb-3">
                        <Label>Reference Number</Label>
                        <Input
                          type="text"
                          value={form.PurchaseRequest_RefNo}
                          onChange={(e) => handleFormChange("PurchaseRequest_RefNo", e.target.value)}
                          placeholder="Enter reference number"
                        />
                      </div>
                    </Col>
                    <Col md={3}>
                      <div className="mb-3">
                        <Label>Invoice Type <span className="text-danger">*</span></Label>
                        <Select
                          key={`invoiceType-${formKey}`}
                          value={invoiceTypeOptions.find(option => option.value === form.PurchaseRequest_InvoiceType_Id) || null}
                          onChange={(option) => handleFormChange("PurchaseRequest_InvoiceType_Id", option?.value || 1)}
                          options={invoiceTypeOptions}
                          styles={selectStyles}
                        />
                      </div>
                    </Col>
                  </Row>

                  <Row>
                    <Col md={3}>
                      <div className="mb-3">
                        <Label>Supplier <span className="text-danger">*</span></Label>
                        <Select
                          key={`supplier-${formKey}`}
                          value={selectedSupplierOption}
                          onChange={(option) => {
                            setSelectedSupplierOption(option);
                            handleFormChange("PurchaseRequest_Supplier_Id", option?.value || null);
                          }}
                          options={supplierSearchOptions}
                          placeholder="Search by name..."
                          styles={selectStyles}
                          isClearable
                          isSearchable
                          maxMenuHeight={200}
                          isLoading={supplierSearchLoading}
                          noOptionsMessage={() => "Type at least 3 characters"}
                          loadingMessage={() => "Searching..."}
                          filterOption={null}
                          onInputChange={(inputValue) => {
                            if (supplierSearchTimeout) {
                              clearTimeout(supplierSearchTimeout);
                            }
                            const trimmedValue = inputValue ? inputValue.trim() : '';
                            const timeout = setTimeout(() => {
                              if (trimmedValue.length >= 3) {
                                searchSuppliersManually(trimmedValue);
                              } else {
                                setSupplierSearchOptions([]);
                              }
                            }, 300);
                            setSupplierSearchTimeout(timeout);
                            return inputValue;
                          }}
                        />
                      </div>
                    </Col>
                  </Row>

                  {/* Details Section */}
                  <Row>
                    <Col md={12}>
                      <Label className="fw-bold mb-3">Purchase Order Details</Label>
                      <div className="table-responsive">
                        <Table bordered style={{ borderCollapse: 'collapse', tableLayout: 'fixed' }}>
                          <thead className="table-light">
                            <tr>
                              <th width="5%" style={{ padding: '8px 12px', border: '1px solid #dee2e6', textAlign: 'center' }}>Sr.No</th>
                              <th width="20%" style={{ padding: '8px 12px', border: '1px solid #dee2e6' }}>Product</th>
                              <th width="10%" style={{ padding: '8px 12px', border: '1px solid #dee2e6' }}>Unit</th>
                              <th width="8%" style={{ padding: '8px 12px', border: '1px solid #dee2e6' }}>Qty</th>
                              <th width="8%" style={{ padding: '8px 12px', border: '1px solid #dee2e6' }}>Bonus Qty</th>
                              <th width="10%" style={{ padding: '8px 12px', border: '1px solid #dee2e6' }}>Unit Price</th>
                              <th width="6%" style={{ padding: '8px 12px', border: '1px solid #dee2e6' }}>Discount %</th>
                              <th width="6%" style={{ padding: '8px 12px', border: '1px solid #dee2e6' }}>Tax %</th>
                              <th width="12%" style={{ padding: '8px 12px', border: '1px solid #dee2e6' }}>Total Amount</th>
                              <th width="12%" style={{ padding: '8px 12px', border: '1px solid #dee2e6' }}>Net Amount</th>
                              <th width="5%" style={{ padding: '8px 12px', border: '1px solid #dee2e6', textAlign: 'center' }}></th>
                            </tr>
                          </thead>
                          <tbody>
                            {details
                              .filter(detail => detail && detail.PurchaseRequestDet_Quantity !== undefined)
                              .map((detail, index) => (
                                <tr key={index}>
                                  <td style={{ padding: '0', textAlign: 'center', border: '1px solid #dee2e6' }}>{index + 1}</td>
                                  <td style={{ padding: '0', border: '1px solid #dee2e6' }}>
                                    <Select
                                      key={`product-${formKey}-${index}`}
                                      value={productOptions.find(option => option.value === detail.PurchaseRequestDet_Product_Id) || null}
                                      onChange={(option) => handleDetailChange(index, 'PurchaseRequestDet_Product_Id', option?.value || null)}
                                      options={productOptions}
                                      placeholder="Select Product"
                                      styles={{
                                        ...selectStyles,
                                        control: (provided) => ({
                                          ...provided,
                                          border: 'none',
                                          borderRadius: '0',
                                          minHeight: '38px',
                                          boxShadow: 'none'
                                        })
                                      }}
                                      isClearable
                                      isSearchable
                                      maxMenuHeight={200}
                                    />
                                  </td>
                                  <td style={{ padding: '0', border: '1px solid #dee2e6' }}>
                                    <Input
                                      type="text"
                                      value={detail.Unit_NameEn || ''}
                                      readOnly
                                      style={{ border: 'none', backgroundColor: 'transparent', padding: '8px 12px' }}
                                    />
                                  </td>
                                  <td style={{ padding: '0', border: '1px solid #dee2e6' }}>
                                    <Input
                                      type="number"
                                      className="no-spinner"
                                      min="0"
                                      step={detail.allowFractional ? '0.0001' : '1'}
                                      value={detail.PurchaseRequestDet_Quantity || 0}
                                      onChange={(e) => handleDetailChange(index, 'PurchaseRequestDet_Quantity', parseFloat(e.target.value) || 0)}
                                      onKeyDown={detail.allowFractional ? undefined : (e) => { if (e.key === '.') e.preventDefault(); }}
                                      style={{ border: 'none', padding: '8px 12px' }}
                                    />
                                  </td>
                                  <td style={{ padding: '0', border: '1px solid #dee2e6' }}>
                                    <Input
                                      type="number"
                                      className="no-spinner"
                                      min="0"
                                      step={detail.allowFractional ? '0.0001' : '1'}
                                      value={detail.PurchaseRequestDet_BonusQuantity || 0}
                                      onChange={(e) => handleDetailChange(index, 'PurchaseRequestDet_BonusQuantity', parseFloat(e.target.value) || 0)}
                                      onKeyDown={detail.allowFractional ? undefined : (e) => { if (e.key === '.') e.preventDefault(); }}
                                      style={{ border: 'none', padding: '8px 12px' }}
                                    />
                                  </td>
                                  <td style={{ padding: '0', border: '1px solid #dee2e6' }}>
                                    <Input
                                      type="number"
                                      className="no-spinner"
                                      min="0"
                                      step="0.01"
                                      value={detail.PurchaseRequestDet_UnitPrice || 0}
                                      onChange={(e) => handleDetailChange(index, 'PurchaseRequestDet_UnitPrice', parseFloat(e.target.value) || 0)}
                                      style={{ border: 'none', padding: '8px 12px' }}
                                    />
                                  </td>
                                  <td style={{ padding: '0', border: '1px solid #dee2e6' }}>
                                    <Input
                                      type="number"
                                      className="no-spinner"
                                      min="0"
                                      step="0.01"
                                      value={detail.PurchaseRequestDet_DiscountPercent || 0}
                                      onChange={(e) => handleDetailChange(index, 'PurchaseRequestDet_DiscountPercent', parseFloat(e.target.value) || 0)}
                                      style={{ border: 'none', padding: '8px 12px' }}
                                    />
                                  </td>
                                  <td style={{ padding: '0', border: '1px solid #dee2e6' }}>
                                    <Input
                                      type="number"
                                      className="no-spinner"
                                      min="0"
                                      max="100"
                                      step="0.01"
                                      placeholder="0"
                                      value={detail.PurchaseRequestDet_TaxPercent === 0 ? '' : detail.PurchaseRequestDet_TaxPercent}
                                      onChange={(e) => {
                                        const val = e.target.value;
                                        handleDetailChange(index, 'PurchaseRequestDet_TaxPercent', val === '' ? 0 : parseFloat(val) || 0);
                                      }}
                                      style={{ border: 'none', padding: '8px 12px' }}
                                    />
                                  </td>
                                  <td style={{ padding: '0', border: '1px solid #dee2e6' }}>
                                    <Input
                                      type="number"
                                      className="no-spinner"
                                      value={detail.PurchaseRequestDet_TotalAmount || 0}
                                      readOnly
                                      style={{ border: 'none', backgroundColor: '#f8f9fa', padding: '8px 12px' }}
                                    />
                                  </td>
                                  <td style={{ padding: '0', border: '1px solid #dee2e6' }}>
                                    <Input
                                      type="number"
                                      className="no-spinner"
                                      value={detail.PurchaseRequestDet_NetAmount || 0}
                                      readOnly
                                      style={{ border: 'none', backgroundColor: '#f8f9fa', padding: '8px 12px' }}
                                    />
                                  </td>
                                  <td style={{ padding: '0', border: '1px solid #dee2e6', textAlign: 'center' }}>
                                    <div className="d-flex gap-1 justify-content-center">
                                      {details.length > 1 && (
                                        <Button
                                          type="button"
                                          color="danger"
                                          size="sm"
                                          onClick={() => removeDetail(index)}
                                          style={{
                                            padding: '6px 10px',
                                            minWidth: '36px',
                                            height: '32px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            borderRadius: '4px'
                                          }}
                                          title="Remove Row"
                                        >
                                          <Minus size={14} />
                                        </Button>
                                      )}
                                    </div>
                                  </td>
                                </tr>
                              ))}
                          </tbody>
                        </Table>
                      </div>
                      <Button
                        type="button"
                        color="success"
                        size="sm"
                        onClick={addDetail}
                        className="mt-2"
                      >
                        <Plus size={16} /> Add Row
                      </Button>
                    </Col>
                  </Row>

                  {/* Summary Section */}
                  <Row className="mt-4">
                    <Col md={6}>
                      <div className="mb-3">
                        <Label>Notes</Label>
                        <Input
                          type="textarea"
                          rows="3"
                          value={form.PurchaseRequest_Notes}
                          onChange={(e) => handleFormChange("PurchaseRequest_Notes", e.target.value)}
                          placeholder="Enter any notes"
                        />
                      </div>
                    </Col>
                    <Col md={12}>
                      <Row>
                        <Col md={4}>
                          <div className="mb-3">
                            <Label>Total Amount</Label>
                            <Input
                              type="number"
                              value={form.PurchaseRequest_TotalAmount}
                              readOnly
                              style={{ backgroundColor: '#f8f9fa' }}
                            />
                          </div>
                        </Col>
                        <Col md={4}>
                          <div className="mb-3">
                            <Label>Total Discount Amount</Label>
                            <Input
                              type="number"
                              value={form.PurchaseRequest_DiscountAmount}
                              readOnly
                              style={{ backgroundColor: '#f8f9fa' }}
                            />
                          </div>
                        </Col>
                        <Col md={4}>
                          <div className="mb-3">
                            <Label className="fw-bold">Net Amount</Label>
                            <Input
                              type="number"
                              value={form.PurchaseRequest_NetAmount}
                              readOnly
                              style={{ backgroundColor: '#e3f2fd', fontWeight: 'bold' }}
                            />
                          </div>
                        </Col>
                      </Row>
                    </Col>
                  </Row>

                  <Row className="mt-4">
                    <Col>
                      <div className="d-flex gap-2">
                        {isPurchaseAllowed && (
                          <Button
                            type="submit"
                            color="primary"
                            disabled={saveLoading}
                            className="waves-effect waves-light"
                          >
                            {saveLoading && (
                              <ClipLoader size={16} color="#ffffff" className="me-2" />
                            )}
                            Save Purchase Order
                          </Button>
                        )}
                        <Button
                          type="button"
                          color="secondary"
                          onClick={handleClear}
                          className="waves-effect waves-light"
                        >
                          Clear
                        </Button>
                        <Button
                          type="button"
                          color="info"
                          onClick={() => navigate('/GmsPurchaseOrder')}
                          className="waves-effect waves-light"
                        >
                          Back to List
                        </Button>
                      </div>
                    </Col>
                  </Row>
                </Form>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
}

